import { useState, useCallback, useRef, useEffect } from 'react'

const MIN_WIDTH = 400
const MAX_WIDTH_PERCENTAGE = 0.8

interface UsePanelResizeProps {
  storageKey: string
  defaultWidth: string
  minWidth?: number
  maxWidthPercentage?: number
}

export const usePanelResize = ({
  storageKey,
  defaultWidth,
  minWidth = MIN_WIDTH,
  maxWidthPercentage = MAX_WIDTH_PERCENTAGE,
}: UsePanelResizeProps) => {
  // Initialize width from localStorage or use default
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const storedWidth = localStorage.getItem(storageKey)
      if (storedWidth) {
        // If it's a percentage, convert to pixels based on current viewport
        if (storedWidth.includes('%')) {
          const percentage = parseFloat(storedWidth) / 100
          const pixelWidth = window.innerWidth * percentage
          return `${Math.max(minWidth, Math.min(pixelWidth, window.innerWidth * maxWidthPercentage))}px`
        }
        return storedWidth
      }
    } catch (error) {
      console.warn('Failed to load panel width from localStorage:', error)
    }

    // Convert default width to pixels if it's a percentage
    if (defaultWidth.includes('%')) {
      const percentage = parseFloat(defaultWidth) / 100
      const pixelWidth = window.innerWidth * percentage
      return `${pixelWidth}px`
    }
    return defaultWidth
  })

  const [isResizing, setIsResizing] = useState(false)
  const resizeStateRef = useRef({
    startX: 0,
    startWidth: 0,
  })

  // Handle resize start
  const handleResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault()

    // Get the panel element
    const panel = (event.currentTarget as HTMLElement).closest(
      '[data-resizable-panel]'
    ) as HTMLElement
    if (!panel) return

    const currentWidth = panel.getBoundingClientRect().width

    resizeStateRef.current = {
      startX: event.clientX,
      startWidth: currentWidth,
    }

    setIsResizing(true)

    // Add visual feedback
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  // Handle mouse move and mouse up
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = resizeStateRef.current.startX - event.clientX
      const newWidth = resizeStateRef.current.startWidth + deltaX

      // Calculate max width based on viewport
      const maxWidth = window.innerWidth * maxWidthPercentage

      // Clamp the width between min and max
      const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))

      // Update panel width
      setPanelWidth(`${clampedWidth}px`)
    }

    const handleMouseUp = () => {
      setIsResizing(false)

      // Reset cursor
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      // Save to localStorage
      localStorage.setItem(storageKey, panelWidth)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, panelWidth, minWidth, maxWidthPercentage, storageKey])

  // Save to localStorage when width changes (debounced)
  useEffect(() => {
    if (!isResizing) {
      localStorage.setItem(storageKey, panelWidth)
    }
  }, [panelWidth, isResizing, storageKey])

  // Handle window resize to ensure panel stays within bounds
  useEffect(() => {
    const handleWindowResize = () => {
      const currentWidthPx = parseInt(panelWidth)
      const maxWidth = window.innerWidth * maxWidthPercentage

      if (currentWidthPx > maxWidth) {
        setPanelWidth(`${maxWidth}px`)
      }
    }

    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [panelWidth, maxWidthPercentage])

  return {
    panelWidth,
    isResizing,
    handleResizeStart,
  }
}
